/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { matchSelectableGuide } from "@packages/media";
import type { SelectableGuide } from "@packages/sdk";
import {
  ModalDropdownMenu,
  ModalDropdownMenuHeader,
  numericPercentages,
  ScrollArea,
  spacing,
  ThemeProvider,
  useIsDesktopViewport,
  useTheme,
  zIndices,
} from "@packages/ui";
import { SelectGuideList } from "@ui/media";
import { useMemo } from "react";

import { PrayerOptionSelectTrigger } from "./PrayerOptionSelectTrigger";

const dropdownOverModalStyle: Interpolation = {
  zIndex: zIndices.dropdownOverModal,
};
const scrollStyle: Interpolation = {
  maxHeight: "400px",
  width: numericPercentages[100],
};
const flexStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  rowGap: spacing.xxs,
};

type PrayerSelectGuideProps = {
  selectedGuides: number[];
  setSelectedGuides: (guides: number[]) => void;
  options: SelectableGuide[];
};

const PrayerSelectGuide = ({
  selectedGuides,
  setSelectedGuides,
  options,
}: PrayerSelectGuideProps) => {
  const isDesktop = useIsDesktopViewport();
  const t = useTranslations();
  const { renderedTheme: parentTheme } = useTheme();

  const guideName = useMemo(() => {
    return (
      options.find((opt) => matchSelectableGuide(opt.guide_ids, selectedGuides))
        ?.name ?? ""
    );
  }, [selectedGuides, options]);

  return (
    <ThemeProvider as={null} forceTheme={isDesktop ? "dark" : parentTheme}>
      <ModalDropdownMenu
        trigger={
          <PrayerOptionSelectTrigger
            label={t("track_settings_guide")}
            value={guideName}
            disabled={options.length <= 1}
          />
        }
        contentCSS={dropdownOverModalStyle}
      >
        <ScrollArea horizontal={false} viewportCSS={scrollStyle}>
          <div css={flexStyle}>
            <ModalDropdownMenuHeader>
              {t("track_settings_guide")}
            </ModalDropdownMenuHeader>
            <SelectGuideList
              onGuideSelection={setSelectedGuides}
              selectedGuides={selectedGuides}
              options={options}
            />
          </div>
        </ScrollArea>
      </ModalDropdownMenu>
    </ThemeProvider>
  );
};

export default PrayerSelectGuide;
